import React, { useState, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import {
  Volume2,
  Loader2,
  AlertCircle,
  Play,
  Pause,
  Download,
} from 'lucide-react'

export function QwenVoiceTool() {
  const { user } = useAuth()
  const [text, setText] = useState('')
  const [speaker, setSpeaker] = useState('Serena')
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)

  const generateSpeech = async () => {
    if (!text.trim() || !user) return

    setGenerating(true)
    setError(null)
    setAudioUrl(null)

    try {
      // Call the generate-speech edge function
      const { data, error: functionError } = await supabase.functions.invoke('generate-speech', {
        body: {
          text: text.trim(),
          speaker: speaker,
        },
      })

      if (functionError) throw functionError

      if (data.error) {
        throw new Error(data.message || data.error)
      }

      // Set the audio URL from the response
      if (data.audio_url) {
        setAudioUrl(data.audio_url)
      } else {
        throw new Error('No audio URL returned from the service')
      }
    } catch (err: any) {
      console.error('Speech generation error:', err)
      setError(err.message || 'An error occurred during speech generation')
    } finally {
      setGenerating(false)
    }
  }

  const togglePlayPause = () => {
    if (!audioRef.current) return

    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      audioRef.current.play()
      setIsPlaying(true)
    }
  }

  const downloadAudio = () => {
    if (!audioUrl) return

    const a = document.createElement('a')
    a.href = audioUrl
    a.download = `qwen-speech-${Date.now()}.mp3`
    a.click()
  }

  const handleAudioEnded = () => {
    setIsPlaying(false)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 mb-2">Qwen Voice</h1>
        <p className="text-slate-600">
          Generate natural-sounding speech from text using Qwen3-TTS
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Input */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Volume2 className="h-5 w-5" />
            Text Input
          </h2>

          <div className="space-y-4">
            {/* Speaker Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Speaker
              </label>
              <select
                value={speaker}
                onChange={(e) => setSpeaker(e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <optgroup label="Female Voices">
                  <option value="Serena">Serena (Default)</option>
                  <option value="Vivian">Vivian</option>
                  <option value="Ono_anna">Ono Anna</option>
                  <option value="Sohee">Sohee</option>
                </optgroup>
                <optgroup label="Male Voices">
                  <option value="Aiden">Aiden</option>
                  <option value="Dylan">Dylan</option>
                  <option value="Eric">Eric</option>
                  <option value="Ryan">Ryan</option>
                  <option value="Uncle_fu">Uncle Fu</option>
                </optgroup>
              </select>
            </div>

            {/* Text Input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Enter text to convert to speech
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type or paste your text here..."
                className="w-full h-40 px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                maxLength={5000}
              />
              <p className="text-xs text-slate-500 mt-1">
                {text.length} / 5000 characters
              </p>
            </div>

            <Button
              onClick={generateSpeech}
              disabled={!text.trim() || generating}
              className="w-full"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating Speech...
                </>
              ) : (
                <>
                  <Volume2 className="h-4 w-4 mr-2" />
                  Generate Speech
                </>
              )}
            </Button>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Error</p>
                  <p className="text-sm">{error}</p>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Right Panel - Audio Player */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Volume2 className="h-5 w-5" />
            Generated Audio
          </h2>

          {audioUrl ? (
            <div className="space-y-4">
              {/* Audio element (hidden) */}
              <audio
                ref={audioRef}
                src={audioUrl}
                onEnded={handleAudioEnded}
                className="hidden"
              />

              {/* Custom audio player UI */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={togglePlayPause}
                    className="h-16 w-16 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-colors shadow-lg"
                  >
                    {isPlaying ? (
                      <Pause className="h-8 w-8" />
                    ) : (
                      <Play className="h-8 w-8 ml-1" />
                    )}
                  </button>
                </div>

                <div className="mt-4 text-center">
                  <p className="text-sm text-slate-600">
                    {isPlaying ? 'Playing...' : 'Ready to play'}
                  </p>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button onClick={downloadAudio} variant="outline" className="flex-1">
                  <Download className="h-4 w-4 mr-2" />
                  Download MP3
                </Button>
              </div>

              {/* Info */}
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-600">
                  <strong>Generated successfully!</strong> Your audio is ready to play or download.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <Volume2 className="h-16 w-16 mb-4" />
              <p>Enter text and click Generate Speech to create audio</p>
            </div>
          )}
        </Card>
      </div>

      {/* Tips */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-slate-800 mb-2">Tips for Best Results</h3>
        <ul className="text-sm text-slate-600 space-y-1">
          <li>• Choose from 9 different voices: 4 female (Serena, Vivian, Ono Anna, Sohee) and 5 male (Aiden, Dylan, Eric, Ryan, Uncle Fu)</li>
          <li>• Clear and well-punctuated text produces more natural speech</li>
          <li>• Maximum text length: 5000 characters</li>
          <li>• Generated audio is in MP3 format</li>
          <li>• Use proper punctuation for natural pauses and intonation</li>
        </ul>
      </Card>
    </div>
  )
}
